import { useCallback, useState } from 'react'

export interface UseKeyboardNavigationOptions<TRecord> {
  records: TRecord[]
  isOpen: boolean
  onSelect: (record: TRecord) => void
  onClose: () => void
  onEscape: () => void
  onClear: () => void
  searchQuery: string
  /**
   * Fired when Enter is pressed while nothing is highlighted (the user typed and hit
   * Enter immediately, without arrowing down to a result first) and there's search text
   * to act on. Lets the host resolve/verify an exact match (e.g. a full code) directly
   * instead of Enter being a no-op.
   */
  onEnterWithoutHighlight?: (searchQuery: string) => void
}

export interface UseKeyboardNavigationResult {
  highlightedIndex: number
  setHighlightedIndex: (index: number) => void
  handleKeyDown: (event: React.KeyboardEvent) => void
}

export function useKeyboardNavigation<TRecord>(
  options: UseKeyboardNavigationOptions<TRecord>
): UseKeyboardNavigationResult {
  const { records, isOpen, onSelect, onClose, onEscape, onClear, searchQuery, onEnterWithoutHighlight } = options
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!isOpen) return

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault()
          if (records.length === 0) return
          setHighlightedIndex((prev) => {
            // Wrap from last to first, or increment
            if (prev >= records.length - 1) return 0
            return prev + 1
          })
          break
        }

        case 'ArrowUp': {
          event.preventDefault()
          if (records.length === 0) return
          setHighlightedIndex((prev) => {
            // Wrap from first to last, or decrement
            if (prev <= 0) return records.length - 1
            return prev - 1
          })
          break
        }

        case 'Home': {
          event.preventDefault()
          if (records.length === 0) return
          setHighlightedIndex(0)
          break
        }

        case 'End': {
          event.preventDefault()
          if (records.length === 0) return
          setHighlightedIndex(records.length - 1)
          break
        }

        case 'Enter': {
          event.preventDefault()
          if (highlightedIndex >= 0 && highlightedIndex < records.length) {
            onSelect(records[highlightedIndex])
          } else if (searchQuery.trim().length > 0) {
            onEnterWithoutHighlight?.(searchQuery)
          }
          break
        }

        case 'Escape': {
          event.preventDefault()
          onEscape()
          break
        }

        case 'Backspace': {
          // Only clear selection if search input is empty
          if (searchQuery === '') {
            onClear()
          }
          // Don't prevent default - allow normal backspace behavior in input
          break
        }

        case 'Tab': {
          // Close dropdown but don't prevent default - allow focus to move to next element
          onClose()
          break
        }

        default:
          break
      }
    },
    [isOpen, records, highlightedIndex, onSelect, onClose, onEscape, onClear, searchQuery, onEnterWithoutHighlight]
  )

  return {
    highlightedIndex,
    setHighlightedIndex,
    handleKeyDown,
  }
}
